import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { FileText, ChevronDown, RotateCcw, Save, Eye, Info } from "lucide-react";

const DOCS = [
  { key: "convenzione", label: "Atto di Convenzione", icon: "📜" },
  { key: "privacy", label: "Informativa Privacy", icon: "🔒" },
  { key: "consenso", label: "Consenso Trattamento Dati", icon: "✍️" },
];

/**
 * Iter122k60 · Editor testi dei 3 modelli di Convenzione Credius.
 * Blocco-per-blocco: mostra il testo default, permette override tenant,
 * ripristino del singolo blocco, anteprima PDF live.
 */
export function CrediusModelliEditor() {
  // Iter122k66 · nessun modello selezionato di default: la lista sezioni resta
  // nascosta finché l'utente non clicca su uno dei 3 pulsanti in alto.
  const [activeDoc, setActiveDoc] = useState(null);
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});          // { block_key: text_editato }
  const [expanded, setExpanded] = useState({});      // { block_key: true/false }
  const [busy, setBusy] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  // Iter122k62 · click-outside listener (no backdrop) per non coprire il pulsante toggle
  const placeholderRef = useRef(null);
  useEffect(() => {
    if (!showPlaceholder) return;
    const onDocClick = (e) => {
      if (placeholderRef.current && !placeholderRef.current.contains(e.target)) {
        setShowPlaceholder(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showPlaceholder]);

  const load = async (doc) => {
    if (!doc) { setData(null); setDrafts({}); setExpanded({}); return; }
    try {
      const r = await api.get(`/credius/template-testi/${doc}`);
      setData(r.data);
      setDrafts({});
      setExpanded({});
    } catch (err) { toast.error("Errore caricamento modello"); }
  };

  useEffect(() => { load(activeDoc); }, [activeDoc]);

  // Iter122k66 · click sulla card attiva → chiude e riporta la vista al menù pulito
  const onDocClick = (key) => setActiveDoc((cur) => (cur === key ? null : key));

  const dirtyCount = Object.keys(drafts).length;

  const salva = async () => {
    if (dirtyCount === 0) { toast.info("Nessuna modifica da salvare"); return; }
    setBusy(true);
    try {
      // Merge overrides esistenti + drafts (drafts hanno priorità)
      const overrides = {};
      for (const b of data.blocks) {
        if (b.is_override) overrides[b.key] = b.text;
      }
      for (const k in drafts) overrides[k] = drafts[k];
      const r = await api.put(`/credius/template-testi/${activeDoc}`, { overrides });
      setData(r.data); setDrafts({});
      toast.success("Modello aggiornato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio non riuscito");
    } finally { setBusy(false); }
  };

  const ripristinaBlocco = async (key) => {
    if (!window.confirm("Ripristinare il testo originale di questo blocco?")) return;
    // Se è solo un draft (non ancora salvato), basta cancellarlo
    if (drafts[key] !== undefined && !data.blocks.find(b => b.key === key)?.is_override) {
      setDrafts((d) => { const c = { ...d }; delete c[key]; return c; });
      return;
    }
    try {
      const r = await api.delete(`/credius/template-testi/${activeDoc}/${key}`);
      setData(r.data);
      setDrafts((d) => { const c = { ...d }; delete c[key]; return c; });
      toast.success("Testo originale ripristinato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Ripristino non riuscito");
    }
  };

  const anteprima = () => activeDoc && openSecureFile(`/credius/template-testi/${activeDoc}/anteprima-pdf`);

  const setDraft = (key, text) => setDrafts((d) => ({ ...d, [key]: text }));

  return (
    <div data-testid="modelli-editor" className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <FileText className="w-5 h-5 text-indigo-800" />
        <h2 className="font-bold text-indigo-950">Modelli documentali di convenzione</h2>
        <span className="text-xs text-slate-500 ml-1">(editabili blocco-per-blocco)</span>
        {/* Iter122k61 · Placeholder come vero menù a tendina compatto (chiuso di default) */}
        {/* Iter122k62 · click-outside via useEffect+ref (no backdrop che coprirebbe il toggle) */}
        <div className="relative" ref={placeholderRef}>
          <button type="button"
            data-testid="placeholder-toggle"
            disabled={!data}
            onClick={() => setShowPlaceholder((s) => !s)}
            className="flex items-center gap-1 text-xs font-bold uppercase px-2.5 py-1.5 rounded-full border-2 border-slate-300 bg-white text-indigo-900 hover:bg-slate-50 h-9 disabled:opacity-40 disabled:cursor-not-allowed">
            <Info className="w-3.5 h-3.5" />
            Placeholder
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showPlaceholder ? "rotate-180" : ""}`} />
          </button>
          {showPlaceholder && data && (
            <div data-testid="placeholder-menu"
              className="absolute right-0 mt-2 w-[320px] sm:w-[520px] z-30 bg-white border-2 border-slate-200 rounded-xl shadow-xl p-3 grid sm:grid-cols-3 gap-3 text-xs">
              {Object.entries(data.placeholders || {}).map(([group, items]) => (
                <div key={group}>
                  <div className="font-bold text-slate-700 mb-1">{group}</div>
                  <ul className="space-y-1">
                    {items.map(([ph, lbl]) => (
                      <li key={ph}>
                        <code className="bg-slate-50 border border-slate-200 px-1 rounded text-[10px] text-indigo-800">{ph}</code>
                        <span className="text-slate-500 ml-1">{lbl}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
        <Button size="sm" onClick={anteprima} data-testid="modelli-anteprima-btn"
          disabled={!activeDoc || !data}
          variant="outline" className="border-indigo-300 text-indigo-800 hover:bg-indigo-50 h-9 disabled:opacity-40 disabled:cursor-not-allowed">
          <Eye className="w-4 h-4 mr-1" /> Anteprima PDF
        </Button>
      </div>
      <p className="text-sm text-slate-500">
        Modifica i 3 modelli allegati automaticamente alla proposta di convenzione. Consulta il menù
        <b> Placeholder</b> qui sopra per l'elenco dei campi dinamici disponibili.
      </p>

      {/* Tab documenti */}
      <div className="flex flex-wrap gap-2" role="tablist">
        {DOCS.map((d) => (
          <button key={d.key} type="button"
            data-testid={`tab-${d.key}`}
            onClick={() => onDocClick(d.key)}
            className={`px-3 py-1.5 text-sm font-bold rounded-full border-2 transition-colors ${
              activeDoc === d.key
                ? "bg-indigo-900 text-white border-indigo-900"
                : "bg-white text-indigo-800 border-indigo-200 hover:bg-indigo-50"
            }`}>
            <span className="mr-1">{d.icon}</span> {d.label}
          </button>
        ))}
      </div>

      {/* Iter122k66 · Nessun modello selezionato: hint minimale, sezioni nascoste */}
      {!activeDoc && (
        <div data-testid="modelli-hint-nessun-doc"
          className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
          Seleziona uno dei 3 modelli in alto per aprire le relative sezioni editabili.
        </div>
      )}

      {/* Toolbar salvataggio (solo con modello attivo) */}
      {activeDoc && dirtyCount > 0 && (
        <div className="sticky top-2 z-10 flex items-center gap-2 bg-amber-50 border-2 border-amber-300 rounded-lg px-3 py-2">
          <span className="text-sm font-bold text-amber-800">{dirtyCount} modifiche non salvate</span>
          <Button size="sm" data-testid="modelli-salva-btn" disabled={busy} onClick={salva}
            className="ml-auto bg-indigo-900 hover:bg-indigo-950 h-9">
            <Save className="w-4 h-4 mr-1" /> Salva modifiche
          </Button>
        </div>
      )}

      {/* Loader in-flight (breve gap tra click sul tab e arrivo dei dati) */}
      {activeDoc && !data && (
        <div className="text-sm text-slate-400">Caricamento…</div>
      )}

      {/* Elenco blocchi raggruppati per sezione (articolo) — Iter122k63 · chiusi di default */}
      {activeDoc && data && (
      <div className="space-y-2">
        {(() => {
          // Raggruppo i blocchi per sezione: ogni "art" apre una nuova sezione.
          // I blocchi prima del primo "art" finiscono nella sezione "Intestazione".
          const sections = [];
          let cur = { title: "Intestazione", key: "__intro__", blocks: [] };
          for (const b of data.blocks) {
            if (b.kind === "art") {
              if (cur.blocks.length > 0) sections.push(cur);
              cur = { title: (b.text || "").replace(/<[^>]*>/g, ""), key: b.key, blocks: [b] };
            } else {
              cur.blocks.push(b);
            }
          }
          if (cur.blocks.length > 0) sections.push(cur);
          return sections.map((sec) => {
            const modifiedInSec = sec.blocks.filter((b) => b.is_override || drafts[b.key] !== undefined).length;
            const isOpen = !!expanded[`__sec__${sec.key}`];
            return (
              <div key={sec.key} data-testid={`section-${sec.key}`}
                className={`rounded-lg border-2 ${modifiedInSec ? "border-amber-300 bg-amber-50/40" : "border-slate-200 bg-white"}`}>
                <button type="button"
                  data-testid={`section-toggle-${sec.key}`}
                  onClick={() => setExpanded((s) => ({ ...s, [`__sec__${sec.key}`]: !s[`__sec__${sec.key}`] }))}
                  className="w-full flex items-center gap-2 text-left px-3 py-2.5">
                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 bg-indigo-100 text-indigo-800">
                    SEZIONE
                  </span>
                  <span className="text-sm font-bold text-slate-800 line-clamp-1 flex-1">{sec.title}</span>
                  <span className="text-[10px] text-slate-500 shrink-0">{sec.blocks.length} blocchi</span>
                  {modifiedInSec > 0 && (
                    <span className="text-[10px] font-bold text-amber-700 shrink-0">{modifiedInSec} MOD.</span>
                  )}
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-2 pb-2 space-y-1 border-t border-slate-100 pt-2">
                    {sec.blocks.map((b, idx) => {
                      const draft = drafts[b.key];
                      const currentText = draft !== undefined ? draft : b.text;
                      const modificato = b.is_override || draft !== undefined;
                      const isTitle = b.kind === "art";
                      const isList = b.kind === "pi";
                      const bOpen = !!expanded[b.key];
                      return (
                        <div key={b.key} data-testid={`block-${b.key}`}
                          className={`rounded-lg border-2 ${modificato ? "border-amber-300 bg-amber-50/50" : "border-slate-100 bg-white"} p-2`}>
                          <button type="button" onClick={() => setExpanded((s) => ({ ...s, [b.key]: !s[b.key] }))}
                            className="w-full flex items-center gap-2 text-left">
                            <span className="text-[10px] font-bold uppercase text-slate-500 shrink-0 w-6">{idx + 1}.</span>
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${
                              isTitle ? "bg-indigo-100 text-indigo-800" : isList ? "bg-slate-100 text-slate-700" : "bg-emerald-100 text-emerald-800"
                            }`}>{isTitle ? "TITOLO" : isList ? "LISTA" : "PARAGR."}</span>
                            <span className={`text-sm text-slate-700 line-clamp-1 flex-1 ${isTitle ? "font-bold" : ""}`}>
                              {currentText.replace(/<[^>]*>/g, "")}
                            </span>
                            {modificato && <span className="text-[10px] font-bold text-amber-700 shrink-0">MODIFICATO</span>}
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${bOpen ? "rotate-180" : ""}`} />
                          </button>
                          {bOpen && (
                            <div className="mt-2 pt-2 border-t border-slate-100 space-y-2">
                              <Textarea
                                data-testid={`block-textarea-${b.key}`}
                                value={currentText}
                                onChange={(e) => setDraft(b.key, e.target.value)}
                                rows={Math.max(3, Math.min(12, Math.ceil(currentText.length / 90)))}
                                className="font-mono text-sm"
                              />
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] text-slate-500">
                                  Tag consentiti: <code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>. Placeholder: usa
                                  <code className="bg-slate-100 px-1 rounded ml-1">{"{chiave}"}</code>.
                                </span>
                                {modificato && (
                                  <Button size="sm" variant="outline" data-testid={`block-reset-${b.key}`}
                                    onClick={() => ripristinaBlocco(b.key)}
                                    className="ml-auto h-8 text-xs border-slate-300 hover:bg-slate-50">
                                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Ripristina originale
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          });
        })()}
      </div>
      )}

      {activeDoc && data && data.updated_at && (
        <div className="text-[11px] text-slate-500 text-right">
          Ultima modifica: {new Date(data.updated_at).toLocaleString("it-IT")}{data.updated_by ? ` · ${data.updated_by}` : ""}
        </div>
      )}
    </div>
  );
}
